import React, { useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
} from '@mui/material';
import AnimatedElement from './AnimatedElement';

const HeroSection = () => {
  const theme = useTheme();
  const sliderRef = useRef(null);

  const sliderImages = [
    {
      id: 5,
      src: "/hero-slider/hero_bild_5.webp",
      alt: "Corps Guestphalia - Bild 5"
    },
  ];

  useEffect(() => {
    const sliderNode = sliderRef.current;

    const initSlider = () => {
      if (window.$ && sliderNode && window.$.fn.slick) {
        try {
          if (window.$(sliderNode).hasClass('slick-initialized')) return;

          window.$(sliderNode).slick({
            dots: false,
            infinite: true,
            speed: 700,
            slidesToShow: 1,
            slidesToScroll: 1,
            autoplay: true,
            autoplaySpeed: 4500,
            centerMode: true,
            centerPadding: '0px',
            arrows: true,
            appendArrows: '#sliderArrows',
            prevArrow: '<button type="button" class="slick-prev custom-arrow"><img src="/icon_3_pfeil_zurueck.png" /></button>',
            nextArrow: '<button type="button" class="slick-next custom-arrow"><img src="/icon_2_pfeil_vor.png" /></button>',
            pauseOnHover: true,
            cssEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
          });
        } catch (error) {
          console.error(error);
        }
      }
    };

    initSlider();

    return () => {
      if (window.$ && sliderNode && window.$.fn.slick) {
        try {
          if (window.$(sliderNode).hasClass('slick-initialized')) {
            window.$(sliderNode).slick('unslick');
          }
        } catch (e) {}
      }
    };
  }, []);

  const scrollToAbout = () => {
    document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      id="hero"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: theme.palette.primary.white,
        color: '#000',
      }}
    >

      {/* Backgrounds */}
      <Box sx={{
        position: 'absolute',
        inset: 0,
        background: `
          radial-gradient(circle at 20% 20%, rgba(58,123,62,0.12), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(0,0,0,0.06), transparent 45%)
        `,
        zIndex: 0,
        pointerEvents: 'none',
      }} />

      <Box sx={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'url(/grain.png)',
        opacity: 0.04,
        zIndex: 0,
        pointerEvents: 'none',
      }} />

      <Container
        maxWidth={false}
        sx={{
          maxWidth: '1600px',
          mx: 'auto',
          px: { xs: 1.5, sm: 2, md: 3 },
          position: 'relative',
          zIndex: 2,
        }}
      >

        {/* TEXT */}
        <AnimatedElement>
          <Box sx={{ textAlign: 'center', pt: { xs: 8, sm: 10, md: 18 }, pb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '4rem' },
                fontWeight: 500,
                mb: { xs: 2.5, md: 4 },
                letterSpacing: '0.02em',
                background: 'linear-gradient(90deg,#000,#3a7b3e,#000)',
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'shine 8s linear infinite',
                '@keyframes shine': {
                  '0%': { backgroundPosition: '0% 50%' },
                  '100%': { backgroundPosition: '200% 50%' },
                },
              }}
            >
              CORPS GUESTPHALIA<br />
              BERLIN
            </Typography>

            <Typography sx={{
              fontSize: { xs: '0.95rem', sm: '1rem', md: '1.2rem' },
              mb: 2,
              opacity: 0.8,
            }}>
              SEIT 1845
            </Typography>
          </Box>
        </AnimatedElement>

        {/* SLIDER */}
        <AnimatedElement>
          <Box sx={{ position: 'relative', mb: 10 }}>

            <Box id="sliderArrows" sx={{
              position: 'absolute',
              left: { xs: 8, sm: 10, md: -26 },
              top: { xs: -10, sm: -8, md: 80 },
              display: 'flex',
              gap: 1,
              zIndex: 3,

              '& .custom-arrow': {
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                width: { xs: 34, sm: 36, md: 40 },
                height: { xs: 34, sm: 36, md: 40 },
                transition: '0.3s',
                '&:hover': { opacity: 0.6 },
                '& img': { width: '100%' },
              },
            }} />

            <Box
              ref={sliderRef}
              sx={{
                maxWidth: { xs: '100%', md: 1200 },
                mx: 'auto',

                // 👇 WICHTIG: feste visuelle Höhe für Hero-Wirkung
                '& .slick-list': {
                  overflow: 'hidden',
                },

                '& .slick-slide': {
                  transform: 'scale(0.95)',
                  opacity: 0.6,
                  transition: 'all 0.6s ease',
                },

                '& .slick-center': {
                  transform: 'scale(1)',
                  opacity: 1,
                },

                // 👇 Bild jetzt wirklich groß & sauber gefittet
                '& img': {
                  width: '100%',
                  height: { xs: 220, sm: 320, md: 520 },
                  objectFit: 'cover',
                  borderRadius: 2,
                  transition: 'transform 1.2s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                },
              }}
            >
              {sliderImages.map((img) => (
                <Box key={img.id}>
                  <img src={img.src} alt={img.alt} />
                </Box>
              ))}
            </Box>

          </Box>
        </AnimatedElement>

      </Container>

      {/* SCROLL */}
      <Box
        onClick={scrollToAbout}
        sx={{
          position: 'absolute',
          bottom: { xs: 12, sm: 18, md: 30 },
          left: '50%',
          transform: 'translateX(-50%)',
          cursor: 'pointer',
          zIndex: 3,
          animation: 'float 2.5s ease-in-out infinite',

          '@keyframes float': {
            '0%,100%': { transform: 'translate(-50%,0)' },
            '50%': { transform: 'translate(-50%,8px)' },
          },

          '&::before': {
            content: '""',
            display: 'block',
            width: 2,
            height: { xs: 28, md: 40 },
            background: '#3a7b3e',
            margin: '0 auto 8px',
          },
        }}
      >
        <Box sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          border: '2px solid #3a7b3e',
        }} />
      </Box>

    </Box>
  );
};

export default HeroSection;
